import React, {Dispatch, SetStateAction, useMemo} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {HStack, Text, Skeleton, StackProps, TextProps, useDisclosure, useMultiStyleConfig} from '@chakra-ui/react'
import {Button, ButtonProps, HideBelowViewport, Icon, ShareBox} from '_uiComponents'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useMinicart, useViewport, useWishlist, useOpenModal, useBasket, useBottomActions} from '_customHooks'
import {getProductAnalyticsInfo} from '_analytics/utils/analyticsUtils'
import {getAbsoluteUrl} from '_utils'
import type {WishlistProduct} from '_types'
import {useGlobalConfigState} from '_context'

interface WishlistButtonsProps {
    products: Array<WishlistProduct>
    productsCount: number
    isLoading?: boolean
    wishlistVariantMapId: Map<string, string>
    onWishlistBuyAllProducts?: (productIds: Array<string>) => void
    setIsActionLoading: Dispatch<SetStateAction<boolean>>
}

/**
 * @name WishlistButtons
 *
 * @component
 *
 * @description
 * Renders a list of buttons about Wishlist.
 *
 * @remarks
 * On mobile there is only 1 button shown.
 *
 * @param {WishlistButtonsProps} props - Properties object.
 * @param {Array<WishlistProduct>} props.products - Mapped products.
 * @param {number} props.productsCount - Count of products in the wishlist.
 * @param {Map<string, string>} props.wishlistVariantMapId - Map that associates a productId to a variantId.
 * @param {boolean} [props.isLoading] - Indicates if wishlist is currently in loading status.
 * @param {function} [props.onWishlistBuyAllProducts] - Callback to catch behaviour of wishlist buy all products.
 * @param {Dispatch<SetStateAction<boolean>>} props.setIsActionLoading - Callback for action loading setter.
 *
 * @returns {React.FC<WishlistButtonsProps>} - Rendered `WishlistButtons` component.
 */
const WishlistButtons: React.FC<WishlistButtonsProps> = ({
    products,
    productsCount,
    wishlistVariantMapId,
    isLoading = false,
    setIsActionLoading,
    onWishlistBuyAllProducts,
}) => {
    const styles = useMultiStyleConfig('WishlistButtons') as Record<string, StackProps & TextProps & ButtonProps>
    const {isOpen: isShareWishlistPopoverOpen, onToggle: onToggleShareWishlistPopover, onClose: onCloseShareWishlistPopover} = useDisclosure()
    const {formatMessage} = useIntl()
    const {locale, site} = useMultiSite()
    const {isMobile} = useViewport()
    const {openWishlistSize} = useOpenModal()
    const {previewMinicart} = useMinicart()
    const {publicWishlistUrl} = useWishlist()
    const {addAllWishlistProductsToCart} = useBasket()
    const {data: customer} = useCurrentCustomer()
    const {appPreferences} = useGlobalConfigState()
    const {hostLink} = appPreferences

    const isPaginationRendered = useMemo(() => !isLoading && productsCount > 0, [isLoading, productsCount])
    const {isPaginationVisible} = useBottomActions(isPaginationRendered)

    const productHasSize = (item: WishlistProduct) => !!item.c_variantData

    const handleSaveWishlist = () => {
        // Redirects the user to login page.
        if (hostLink && customer?.isGuest) {
            const hostLinkLocalized = JSON.parse(hostLink)[site.id]
            window.location.href = getAbsoluteUrl(hostLinkLocalized, locale.alias, '/login')
        }
    }

    /**
     * Handles the buy products button behaviour.
     *
     * @description
     * - If there is at least 1 product that doesn't have a selected size it opens the `WishlistSizeModal`.
     * - All the products that have a selected size are moved into the basket and removed from the wishlist.
     */
    const handleBuyProducts = async () => {
        const productItemsWithoutSize: Array<{productId: string}> = []
        const productItemsWithSize: Array<{productId: string; quantity: number; variantId?: string; price?: number}> = []

        products.forEach((item) => {
            if (!productHasSize(item)) {
                const variantId = wishlistVariantMapId.get(item.id)
                if (variantId) {
                    const {productPriceNumber: price} = getProductAnalyticsInfo(item)
                    productItemsWithSize.push({productId: item.id, quantity: 1, variantId: variantId, price})
                } else {
                    productItemsWithoutSize.push({productId: item.id})
                }
            } else {
                const {productPriceNumber: price} = getProductAnalyticsInfo(item)
                productItemsWithSize.push({productId: item.id, quantity: 1, price})
            }
        })

        try {
            if (productItemsWithSize.length > 0) {
                setIsActionLoading(true)
                await addAllWishlistProductsToCart(productItemsWithSize)
                onWishlistBuyAllProducts?.(productItemsWithSize.map((p) => p.productId))
            }
        } catch (error) {
            console.error(error)
        } finally {
            setIsActionLoading(false)
            if (productItemsWithoutSize.length > 0) {
                openWishlistSize({
                    shouldOpenMiniCartOnClose: productItemsWithSize.length > 0,
                })
            } else if (productItemsWithoutSize.length === 0 && productItemsWithSize.length > 0) {
                previewMinicart()
            }
        }
    }

    return (
        <HStack data-testid="wishlist-buttons" {...styles.buttons}>
            {isLoading ? (
                <WishlistButtonsSkeleton />
            ) : (
                <>
                    <Button size="sm" variant="secondary" {...(styles.wishlistButton as ButtonProps)} onClick={handleSaveWishlist}>
                        <FormattedMessage id="label.wishlist.save" defaultMessage={'Save Wishlist'} />
                    </Button>

                    <ShareBox
                        isOpen={isShareWishlistPopoverOpen}
                        onClose={onCloseShareWishlistPopover}
                        shareUrl={publicWishlistUrl}
                        shareSubject={formatMessage({
                            id: 'label.wishlist.share.email.subject',
                            defaultMessage: 'I would like to share my wishlist with you',
                        })}
                    >
                        <HideBelowViewport
                            breakpoint="md"
                            as={Button}
                            size="sm"
                            variant="secondary"
                            w={'fit-content'}
                            rightIcon={<Icon name="IconShare" size="sm" />}
                            onClick={onToggleShareWishlistPopover}
                        >
                            <FormattedMessage id="label.wishlist.share" defaultMessage={'Share Wishlist'} />
                        </HideBelowViewport>
                    </ShareBox>

                    <HideBelowViewport breakpoint="md">
                        <Button size="sm" variant="secondary" onClick={handleBuyProducts}>
                            <FormattedMessage id="labal.addall.products" defaultMessage={'Buy {0} products'} values={{0: productsCount}} />
                        </Button>
                    </HideBelowViewport>

                    {isMobile && (
                        <>
                            <HStack
                                data-testid="mobile-actions-wrapper"
                                {...styles.mobileActionsWrapper}
                                {...(!isPaginationVisible ? styles.hiddenMobileActionsWrapper : {})}
                            >
                                <HStack as="button" {...styles.shareWishlistMobileButton} onClick={onToggleShareWishlistPopover}>
                                    <Text {...styles.mobileButtonText}>
                                        <FormattedMessage id="label.wishlist.share" defaultMessage={'Share Wishlist'} /> <Icon name="IconShare" size="sm" />
                                    </Text>
                                </HStack>
                                <HStack as="button" {...styles.buyAllProductsMobileButton} onClick={handleBuyProducts}>
                                    <Text {...styles.mobileButtonText}>
                                        <FormattedMessage id="labal.addall.products" defaultMessage={'Buy {0} products'} values={{0: productsCount}} />
                                    </Text>
                                </HStack>
                            </HStack>
                        </>
                    )}
                </>
            )}
        </HStack>
    )
}

/**
 * @name WishlistButtonsSkeleton
 *
 * @component
 *
 * @description
 * Renders skeletons for WishlistButtons.
 *
 * @returns {JSX.Element}
 */
const WishlistButtonsSkeleton = (): JSX.Element => {
    return (
        <>
            <Skeleton data-testid="wishlist-buttons-skeleton-1" w={'145px'} h={'48px'} />

            <HideBelowViewport breakpoint="md">
                <Skeleton data-testid="wishlist-buttons-skeleton-2" w={'210px'} h={'48px'} />
            </HideBelowViewport>

            <HideBelowViewport breakpoint="md">
                <Skeleton data-testid="wishlist-buttons-skeleton-3" w={'190px'} h={'48px'} />
            </HideBelowViewport>
        </>
    )
}

export {WishlistButtons, WishlistButtonsSkeleton}
